import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Typography,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'
import {
  NotificationsActive as NotificationsActiveIcon,
  Settings as SettingsIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  AccessTime as AccessTimeIcon,
  CalendarMonth as CalendarMonthIcon,
  Business as BusinessIcon,
} from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import ReminderService from '../services/reminderService'
import type { ReminderUpdateScheduleRequest, TenantReminderConfigSummary } from '../types/reminder'

// スケジュールタイプ
type ScheduleType = 'daily' | 'weekdays' | 'weekly'

// 曜日の定義
const WEEKDAY_KEYS = [
  { value: 1, key: 'monday' },
  { value: 2, key: 'tuesday' },
  { value: 3, key: 'wednesday' },
  { value: 4, key: 'thursday' },
  { value: 5, key: 'friday' },
  { value: 6, key: 'saturday' },
  { value: 0, key: 'sunday' },
]

// 時刻の選択肢を生成
const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${i.toString().padStart(2, '0')}:00`,
}))

// 通知日数の選択肢（0 = 当日）
const NOTIFICATION_DAYS = [0, 1, 2, 3, 5, 7, 10, 14, 21, 30]

// 通知日数のラベルを取得（0の場合は「当日」）
const getDaysBeforeLabel = (
  days: number,
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  return days === 0 ? t('schedule.sameDay') : t('schedule.daysBefore', { days })
}

// Cron式からスケジュール設定を解析
const parseCronToSchedule = (
  cron: string | undefined | null
): {
  type: ScheduleType
  hour: number
  weekday: number
} => {
  if (!cron) {
    return { type: 'daily', hour: 10, weekday: 1 }
  }
  const parts = cron.split(' ')
  if (parts.length !== 5) {
    return { type: 'daily', hour: 10, weekday: 1 }
  }

  const hour = parseInt(parts[1]) || 10
  const dayOfWeek = parts[4]

  if (dayOfWeek === '*') {
    return { type: 'daily', hour, weekday: 1 }
  } else if (dayOfWeek === '1-5') {
    return { type: 'weekdays', hour, weekday: 1 }
  } else {
    const weekday = parseInt(dayOfWeek) || 1
    return { type: 'weekly', hour, weekday }
  }
}

// スケジュール設定からCron式を生成
const scheduleToCron = (type: ScheduleType, hour: number, weekday: number): string => {
  switch (type) {
    case 'daily':
      return `0 ${hour} * * *`
    case 'weekdays':
      return `0 ${hour} * * 1-5`
    case 'weekly':
      return `0 ${hour} * * ${weekday}`
    default:
      return `0 ${hour} * * *`
  }
}

// スケジュールの説明文を生成
const getScheduleDescription = (
  type: ScheduleType,
  hour: number,
  weekday: number,
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  const timeStr = `${hour.toString().padStart(2, '0')}:00`
  switch (type) {
    case 'daily':
      return t('scheduleDescription.daily', { time: timeStr })
    case 'weekdays':
      return t('scheduleDescription.weekdays', { time: timeStr })
    case 'weekly':
      const dayKey = WEEKDAY_KEYS.find((d) => d.value === weekday)?.key || 'monday'
      const dayName = t(`weekdays.${dayKey}`)
      return t('scheduleDescription.weekly', { weekday: dayName, time: timeStr })
    default:
      return ''
  }
}

/**
 * SUPER_ADMIN専用: テナント別リマインダー管理ページ
 */
export const TenantReminderManagePage: React.FC = () => {
  const { t } = useTranslation('reminder')
  const { user } = useAuth()

  // 状態管理
  const [tenants, setTenants] = useState<TenantReminderConfigSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null)
  const [tenantActionLoading, setTenantActionLoading] = useState<string | null>(null)

  // ダイアログ状態
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false)

  // スケジュールフォーム状態
  const [scheduleType, setScheduleType] = useState<ScheduleType>('daily')
  const [scheduleHour, setScheduleHour] = useState(10)
  const [scheduleWeekday, setScheduleWeekday] = useState(1)
  const [selectedDays, setSelectedDays] = useState<number[]>([7, 3, 1])

  // 権限チェック
  const isSuperAdmin = user?.role === 'SUPER_ADMIN'

  // データ取得関数
  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      const tenantsData = await ReminderService.getAllTenantsConfig()
      setTenants(tenantsData.tenants)
    } catch (error: unknown) {
      console.error('Failed to fetch tenant reminder data:', error)
      const err = error as { response?: { data?: { error?: string } }; message?: string }
      setError(err.response?.data?.error || err.message || t('messages.loadError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  // 初期データ取得
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // 権限がない場合は警告を表示
  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t('noPermission')}</Alert>
      </Box>
    )
  }

  // テナントの有効/無効切り替え
  const handleToggleTenant = async (tenantId: string, currentEnabled: boolean) => {
    try {
      setTenantActionLoading(tenantId)

      if (currentEnabled) {
        await ReminderService.disableForTenant(tenantId)
      } else {
        await ReminderService.enableForTenant(tenantId)
      }

      setError('')
      await fetchData()
    } catch (error: unknown) {
      console.error('Failed to toggle tenant reminder:', error)
      const err = error as { response?: { data?: { error?: string } }; message?: string }
      setError(err.response?.data?.error || err.message || t('messages.toggleError'))
    } finally {
      setTenantActionLoading(null)
    }
  }

  // テナントの手動実行
  const handleExecuteTenant = async (tenantId: string) => {
    try {
      setTenantActionLoading(tenantId)

      await ReminderService.executeNowForTenant(tenantId)

      setError('')
      await fetchData()
    } catch (error: unknown) {
      console.error('Failed to execute tenant reminder:', error)
      const err = error as { response?: { data?: { error?: string } }; message?: string }
      setError(err.response?.data?.error || err.message || t('messages.executeError'))
    } finally {
      setTenantActionLoading(null)
    }
  }

  // テナント設定ダイアログを開く
  const handleOpenTenantSettings = (tenant: TenantReminderConfigSummary) => {
    setSelectedTenantId(tenant.tenantId)
    const parsed = parseCronToSchedule(tenant.schedule)
    setScheduleType(parsed.type)
    setScheduleHour(parsed.hour)
    setScheduleWeekday(parsed.weekday)
    // 日数設定をテナントの設定から復元
    const days: number[] = []
    if (tenant.weekBeforeEnabled) days.push(7)
    if (tenant.threeDaysBeforeEnabled) days.push(3)
    if (tenant.dayBeforeEnabled) days.push(1)
    setSelectedDays(days.length > 0 ? days : [7, 3, 1])
    setScheduleDialogOpen(true)
  }

  // テナント設定を更新
  const handleUpdateTenantSchedule = async () => {
    if (!selectedTenantId) return

    try {
      setActionLoading(true)

      if (selectedDays.length === 0) {
        setError(t('dialog.selectAtLeastOne'))
        setActionLoading(false)
        return
      }

      const cronExpression = scheduleToCron(scheduleType, scheduleHour, scheduleWeekday)

      // daysBefore 配列を個別フラグに変換
      const request: ReminderUpdateScheduleRequest = {
        schedule: cronExpression,
        weekBeforeEnabled: selectedDays.includes(7),
        threeDaysBeforeEnabled: selectedDays.includes(3),
        dayBeforeEnabled: selectedDays.includes(1),
        sameDayEnabled: selectedDays.includes(0),
      }

      await ReminderService.updateScheduleForTenant(selectedTenantId, request)

      setScheduleDialogOpen(false)
      setSelectedTenantId(null)
      setError('')
      await fetchData()
    } catch (error: unknown) {
      console.error('Failed to update tenant schedule:', error)
      const err = error as { response?: { data?: { error?: string } }; message?: string }
      setError(err.response?.data?.error || err.message || t('messages.updateError'))
    } finally {
      setActionLoading(false)
    }
  }

  // 通知日数のトグル
  const handleDayToggle = (day: number) => {
    setSelectedDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]))
  }

  // 日時のフォーマット
  const formatDateTime = (dateStr: string | null): string => {
    if (!dateStr) return '-'
    try {
      return new Date(dateStr).toLocaleString('ja-JP')
    } catch {
      return dateStr
    }
  }

  // テナントのスケジュール説明を取得
  const getTenantScheduleDescription = (schedule: string | undefined | null): string => {
    const parsed = parseCronToSchedule(schedule)
    return getScheduleDescription(parsed.type, parsed.hour, parsed.weekday, t)
  }

  // ローディング表示
  if (loading && tenants.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* タイトル行 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('tenants.title')}
        </Typography>
        <IconButton onClick={fetchData} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* ローディング表示（データ更新時） */}
      {loading && tenants.length > 0 && <LinearProgress sx={{ mb: 3 }} />}

      {/* テナント一覧テーブル */}
      <Card>
        <CardHeader
          avatar={<BusinessIcon />}
          subheader={t('tenants.subtitle', { count: tenants.length })}
        />
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('tenants.tenantName')}</TableCell>
                  <TableCell align="center">{t('tenants.status')}</TableCell>
                  <TableCell>{t('tenants.schedule')}</TableCell>
                  <TableCell>{t('tenants.lastExecution')}</TableCell>
                  <TableCell>{t('tenants.nextExecution')}</TableCell>
                  <TableCell align="center">{t('tenants.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.tenantId} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {tenant.tenantName}
                        </Typography>
                        {tenant.isCustomized && (
                          <Chip
                            label={t('tenants.customized')}
                            size="small"
                            variant="outlined"
                            color="info"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={tenant.enabled ? t('schedule.enabled') : t('schedule.disabled')}
                        color={tenant.enabled ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getTenantScheduleDescription(tenant.schedule)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateTime(tenant.lastExecution)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateTime(tenant.nextExecution)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip
                          title={tenant.enabled ? t('schedule.disable') : t('schedule.enable')}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleToggleTenant(tenant.tenantId, tenant.enabled)}
                            disabled={tenantActionLoading === tenant.tenantId}
                            color={tenant.enabled ? 'warning' : 'primary'}
                          >
                            {tenant.enabled ? (
                              <StopIcon fontSize="small" />
                            ) : (
                              <PlayArrowIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('tenants.execute')}>
                          <IconButton
                            size="small"
                            onClick={() => handleExecuteTenant(tenant.tenantId)}
                            disabled={tenantActionLoading === tenant.tenantId || !tenant.enabled}
                            color="primary"
                          >
                            <NotificationsActiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('tenants.settings')}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenTenantSettings(tenant)}
                            disabled={tenantActionLoading === tenant.tenantId}
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {tenants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        {t('tenants.noTenants')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* スケジュール設定ダイアログ */}
      <Dialog
        open={scheduleDialogOpen}
        onClose={() => {
          setScheduleDialogOpen(false)
          setSelectedTenantId(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('dialog.scheduleTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* 実行タイミング */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarMonthIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('dialog.timing')}
                  </Typography>
                </Box>

                <FormControl fullWidth>
                  <InputLabel>{t('dialog.frequency')}</InputLabel>
                  <Select
                    value={scheduleType}
                    label={t('dialog.frequency')}
                    onChange={(e) => setScheduleType(e.target.value as ScheduleType)}
                  >
                    <MenuItem value="daily">{t('frequency.daily')}</MenuItem>
                    <MenuItem value="weekdays">{t('frequency.weekdays')}</MenuItem>
                    <MenuItem value="weekly">{t('frequency.weekly')}</MenuItem>
                  </Select>
                </FormControl>

                {scheduleType === 'weekly' && (
                  <FormControl fullWidth>
                    <InputLabel>{t('dialog.weekday')}</InputLabel>
                    <Select
                      value={scheduleWeekday}
                      label={t('dialog.weekday')}
                      onChange={(e) => setScheduleWeekday(e.target.value as number)}
                    >
                      {WEEKDAY_KEYS.map((day) => (
                        <MenuItem key={day.value} value={day.value}>
                          {t(`weekdays.${day.key}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <FormControl fullWidth>
                  <InputLabel>{t('dialog.time')}</InputLabel>
                  <Select
                    value={scheduleHour}
                    label={t('dialog.time')}
                    onChange={(e) => setScheduleHour(e.target.value as number)}
                  >
                    {HOURS.map((hour) => (
                      <MenuItem key={hour.value} value={hour.value}>
                        {hour.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <AccessTimeIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {getScheduleDescription(scheduleType, scheduleHour, scheduleWeekday, t)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Divider />

            {/* 通知日数 */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NotificationsActiveIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('dialog.notificationDays')}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {t('dialog.notificationDaysHelp')}
                </Typography>

                <FormGroup>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {NOTIFICATION_DAYS.map((day) => (
                      <FormControlLabel
                        key={day}
                        control={
                          <Checkbox
                            checked={selectedDays.includes(day)}
                            onChange={() => handleDayToggle(day)}
                            size="small"
                          />
                        }
                        label={getDaysBeforeLabel(day, t)}
                        sx={{
                          border: 1,
                          borderColor: selectedDays.includes(day) ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          px: 1,
                          m: 0,
                          backgroundColor: selectedDays.includes(day)
                            ? 'primary.50'
                            : 'transparent',
                        }}
                      />
                    ))}
                  </Box>
                </FormGroup>

                {selectedDays.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('dialog.selected')}:
                    </Typography>
                    {selectedDays
                      .sort((a, b) => b - a)
                      .map((day) => (
                        <Chip
                          key={day}
                          label={getDaysBeforeLabel(day, t)}
                          size="small"
                          color="primary"
                          onDelete={() => handleDayToggle(day)}
                        />
                      ))}
                  </Box>
                )}

                {selectedDays.length === 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {t('dialog.selectAtLeastOne')}
                  </Alert>
                )}
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setScheduleDialogOpen(false)
              setSelectedTenantId(null)
            }}
          >
            {t('common:buttons.cancel')}
          </Button>
          <Button
            onClick={handleUpdateTenantSchedule}
            variant="contained"
            disabled={actionLoading || selectedDays.length === 0}
          >
            {t('common:buttons.update')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TenantReminderManagePage
